/* eslint-disable no-unused-vars */
import React, { useCallback, useMemo, useEffect, useState, useLayoutEffect } from 'react';
import queryString from 'query-string';
import { mergeClasses } from '@fluentui/react-components';
import { Overlay } from '@bingads-webui-react/fabric-control';
import { AdsStudioSpinner } from '@bingads-webui-campaign-react/ads-studio-app-shared-components';
import { setClarityTag } from '@bingads-webui-universal/clarity';
import { Mode } from '@bingads-webui-react/app-layout-container';

import { useEditImageState } from './model/use-edit-image-state';
import { ViewName } from './model/utils';
import { ImageAdjustView, AdPlacementListView, SaveButtonInSummaryBar, NavigationView, BackgroundChangeView, PlacementEditView, UpdateConfirmDialog } from './views';
import { useStyles } from './style';

export const EditImagePageComponent = ({
  dataService,
  imageId,
  search,
  i18n,
  pagePerfMarker: editPagePerfMarker,
  permissions,
  logger,
  getSmartCroppings,
  MultiLineEllipsisText,
  navigation,
  isAIGCRegionRestricted,
  notificationApi,
  getAssetEnhancementSettings,
  changeLayout,
  setDataLoaded,
}) => {
  const classes = useStyles();
  const queries = queryString.parse(search);
  const {
    folderId = '', editImageUrl = '', initialCurrentView = '',
  } = queries || {};

  const {
    originalImage,
    resultImage,
    currentView,
    setCurrentView,
    filterIndex,
    setFilterIndex,
    backgroundBlurEnabled,
    toggleBackgroundBlur,
    changeBackgroundBlurLevel,
    backgroundBlurLevel,
    getImagesWithFilter,
    loadingStatus,
    initialAssetLoading,
    saveImageDirectly,
    imagesWithFilter,
    placementList,
    firstImage,
    onEditPlacement,
    editingPlacementInfo,
    adType,
    showRequiredOnly,
    previewTexts,
    previewImage,
    maxCropBox,
    saveCropPlacement,
    cancelCropPlacement,
    switchAdType,
    switchShowRequiredOnly,
    // Background change view states
    getSegmentations,
    generateBackground,
    drawColorBackground,
    imageUrlWithColoredBackground,
    imageUrlWithoutBackground,
    imageUrlWithGeneratedBackground,
    setImageUrlWithGeneratedBackground,
    getMaskValueAtPoint,
    segmentationMasks,
    setSelectedImageSrc,
    selectedImageSrc, // the image after user selected objects
    selectedMasks,
    setSelectedMasks,
    maskValues,
    fetchSegmentationError,
    isFetchingSegmentations,
    replaceBackground,
    saveAsCopy,
    isStockImage,
    isImageUpdated,
    disableSave,
    haveCroppingTasks,
    isConfirmDialogOpen,
    setIsConfirmDialogOpen,
    onConfirmDialogSave,
    allSizeInvalidRatioList,
    saveButtonTooltip,
    updateAdPlacementImage,
  } = useEditImageState({
    imageId,
    folderId,
    dataService,
    editImageUrl,
    i18n,
    perfMarker: editPagePerfMarker,
    permissions,
    logger,
    getSmartCroppings,
    navigation,
    initialCurrentView,
    getAssetEnhancementSettings,
    setDataLoaded,
  });

  const adjustButtonTooltip = useMemo(() => {
    if (isStockImage) {
      return i18n.getString(_TL_('You cannot adjust or edit Shutterstock images.'));
    }
    return undefined;
  }, [i18n, isStockImage]);

  const isSaveAsCopyDisabled = useMemo(() => isStockImage || !isImageUpdated, [isImageUpdated, isStockImage]);

  const baseButtonText = useMemo(() => {
    if (isImageUpdated) {
      return i18n.getString(_TL_('Save as copy'));
    }
    return i18n.getString(_TL_('Save'));
  }, [i18n, isImageUpdated]);

  const onBaseButtonClick = useCallback(() => {
    if (isImageUpdated) {
      saveAsCopy();
    } else {
      saveImageDirectly();
    }
  }, [isImageUpdated, saveAsCopy, saveImageDirectly]);

  const goToBackgroundChangeView = useCallback(() => {
    setCurrentView(ViewName.BackgroundChangeView);
  }, [setCurrentView]);

  const goToImageAdjustView = useCallback(() => {
    setCurrentView(ViewName.ImageAdjustView);
    setImageUrlWithGeneratedBackground(null);
  }, [setCurrentView, setImageUrlWithGeneratedBackground]);

  const gotoPlacementEditView = useCallback((placement) => {
    onEditPlacement(placement);
    setCurrentView(ViewName.PlacementEditView);
  }, [setCurrentView, onEditPlacement]);

  const goToPlacementListView = useCallback(() => {
    setCurrentView(ViewName.PlacementListView);
  }, [setCurrentView]);

  useEffect(() => {
    setClarityTag({ LoadAdsStudioImageEditingPage: true });
  }, []);

  useLayoutEffect(() => {
    if (changeLayout) {
      changeLayout(
        {
          nav: {
            mode: Mode.Hidden,
          },
        },
        true, // isPageConfig
        true // isPageConfigPartialUpdate
      );
    }
  }, [changeLayout]);

  return (
    <div className={mergeClasses('edit--page-container', classes.editImagePage, 'edit-image-page')}>
      <SaveButtonInSummaryBar
        i18n={i18n}
        onSaveDirectly={saveImageDirectly}
        onSaveAsCopy={saveAsCopy}
        isSaveAsCopyDisabled={isSaveAsCopyDisabled}
        isSaveDisabled={disableSave}
        haveCroppingTasks={haveCroppingTasks}
        isBaseButton
        baseButtonText={baseButtonText}
        onBaseButtonClick={onBaseButtonClick}
        saveButtonTooltip={saveButtonTooltip}
      />
      {!initialAssetLoading &&
        <div className={mergeClasses(classes.navigationView, 'navigation-view')}>
          <NavigationView
            i18n={i18n}
            currentView={currentView}
            setCurrentView={setCurrentView}
            isAdjustButtonDisabled={isStockImage}
            adjustButtonTooltip={adjustButtonTooltip}
            updateAdPlacementImage={updateAdPlacementImage}
          />
        </div>
      }
      <div className={classes.navigationPlaceholder} />
      <div className={classes.contentWrapper}>
        {
          currentView === ViewName.PlacementListView &&
          <AdPlacementListView
            placementList={placementList}
            firstImage={firstImage}
            onEditPlacement={gotoPlacementEditView}
            adType={adType}
            showRequiredOnly={showRequiredOnly}
            previewTexts={previewTexts}
            i18n={i18n}
            switchAdType={switchAdType}
            switchShowRequiredOnly={switchShowRequiredOnly}
          />
        }
        {
          currentView === ViewName.PlacementEditView &&
          <PlacementEditView
            i18n={i18n}
            firstImage={firstImage}
            adType={adType}
            {...editingPlacementInfo}
            logger={logger}
            previewImage={previewImage}
            previewTexts={previewTexts}
            goToPlacementListView={goToPlacementListView}
            maxCropBox={maxCropBox}
            onSave={saveCropPlacement}
            onCancel={cancelCropPlacement}
          />
        }
        {
          currentView === ViewName.ImageAdjustView &&
          <ImageAdjustView
            resultImage={resultImage}
            toggleBackgroundBlur={toggleBackgroundBlur}
            setFilterIndex={setFilterIndex}
            backgroundBlurEnabled={backgroundBlurEnabled}
            changeBackgroundBlurLevel={changeBackgroundBlurLevel}
            backgroundBlurLevel={backgroundBlurLevel}
            imagesWithFilter={imagesWithFilter}
            i18n={i18n}
            originalImage={originalImage}
            getImagesWithFilter={getImagesWithFilter}
            goToBackgroundChangeView={goToBackgroundChangeView}
            filterIndex={filterIndex}
            isAIGCRegionRestricted={isAIGCRegionRestricted}
            notificationApi={notificationApi}
            permissions={permissions}
          />
        }
        {
          currentView === ViewName.BackgroundChangeView &&
          <BackgroundChangeView
            i18n={i18n}
            getSegmentations={getSegmentations}
            generateBackground={generateBackground}
            MultiLineEllipsisText={MultiLineEllipsisText}
            drawColorBackground={drawColorBackground}
            imageUrlWithColoredBackground={imageUrlWithColoredBackground}
            imageUrlWithoutBackground={imageUrlWithoutBackground}
            imageUrlWithGeneratedBackground={imageUrlWithGeneratedBackground}
            setImageUrlWithGeneratedBackground={setImageUrlWithGeneratedBackground}
            goToImageAdjustView={goToImageAdjustView}
            resultImage={resultImage} // The original image is the result image from the image adjust view
            originalImage={originalImage}
            logger={logger}
            getMaskValueAtPoint={getMaskValueAtPoint}
            segmentationMasks={segmentationMasks}
            maskValues={maskValues}
            setSelectedImageSrc={setSelectedImageSrc}
            selectedImageSrc={selectedImageSrc}
            selectedMasks={selectedMasks}
            setSelectedMasks={setSelectedMasks}
            fetchSegmentationError={fetchSegmentationError}
            isFetchingSegmentations={isFetchingSegmentations}
            onApplyImage={replaceBackground}
          />
        }
        <UpdateConfirmDialog
          i18n={i18n}
          open={isConfirmDialogOpen}
          setOpen={setIsConfirmDialogOpen}
          onSave={onConfirmDialogSave}
          invalidRatioList={allSizeInvalidRatioList}
        />
        {
          loadingStatus &&
          <Overlay>
            <AdsStudioSpinner className="page-load-spinner" useCustomSize customStyle={{ margin: 200 }} />
          </Overlay>
        }
      </div>
    </div>
  );
};
